
import { motion } from 'framer-motion';
import { useInView } from '../../../hooks/useInView';

const milestones = [
  {
    year: '1989',
    title: 'Założenie Firmy',
    description:
      'Firma P.W. EUREKA zostaje założona w grudniu 1989 roku. Początkowa działalność handlowa.',
    icon: 'ri-flag-line',
  },
  {
    year: '1991',
    title: 'Usługi Antykorozyjne',
    description:
      'Rozpoczęcie działalności w zakresie usług antykorozyjnych wysokościowych na terenie województwa śląskiego.',
    icon: 'ri-paint-brush-line',
  },
  {
    year: '1996',
    title: 'Konstrukcje Stalowe',
    description:
      'Przeniesienie na Podkarpacie i zmiana profilu na wykonawstwo konstrukcji stalowych. Eksport oranżerii do Wielkiej Brytanii.',
    icon: 'ri-building-line',
  },
  {
    year: '2007',
    title: 'Własna Siedziba',
    description:
      'Zakup budynku produkcyjnego wraz z gruntem. Rozpoczęcie rozbudowy pomieszczeń produkcyjnych i biurowych.',
    icon: 'ri-home-gear-line',
  },
  {
    year: '2015',
    title: 'Modernizacja Parku Maszynowego',
    description:
      'Zakup nowoczesnych urządzeń: sprężarka Compaic, maszyna Geka, agregat malarski hydrodynamiczny.',
    icon: 'ri-settings-3-line',
  },
  {
    year: '2024',
    title: 'Innowacje B+R',
    description:
      'Realizacja projektu badawczego — innowacyjny mostek rowerowy z tłumieniem drgań. Dofinansowanie z Funduszy Europejskich.',
    icon: 'ri-lightbulb-line',
  },
];

export default function Timeline() {
  const [ref, isInView] = useInView({ threshold: 0.1 });

  return (
    <section
      ref={ref}
      className="relative py-20 sm:py-32 bg-[#111111] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 sm:mb-20"
        >
          <span
            className="text-[#00E5FF] text-xs tracking-[0.3em] uppercase font-semibold"
            style={{ fontFamily: 'Orbitron, sans-serif' }}
          >
            Kamienie Milowe
          </span>
          <h2
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mt-4 leading-tight"
            style={{ fontFamily: 'Rajdhani, sans-serif' }}
          >
            Nasza <span className="text-[#00E5FF]">Droga</span>
          </h2>
        </motion.div>

        <div className="relative">
          {/* Vertical line — desktop only */}
          <div className="absolute left-1/2 top-0 bottom-0 w-[2px] bg-gradient-to-b from-[#00E5FF]/60 via-[#00E5FF]/30 to-[#00E5FF]/60 hidden md:block -translate-x-1/2"></div>

          {/* Mobile line */}
          <div className="absolute left-6 top-0 bottom-0 w-[2px] bg-gradient-to-b from-[#00E5FF]/60 via-[#00E5FF]/30 to-[#00E5FF]/60 md:hidden"></div>

          <div className="space-y-12 md:space-y-20">
            {milestones.map((milestone, index) => {
              const isLeft = index % 2 === 0;

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 40 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.7, delay: index * 0.15 }}
                  className="relative"
                >
                  {/* Desktop layout */}
                  <div className="hidden md:grid md:grid-cols-2 md:gap-16 items-center">
                    <div
                      className={
                        isLeft ? 'text-right pr-12' : 'order-2 pl-12'
                      }
                    >
                      <div
                        className={`inline-block ${
                          isLeft ? 'text-right' : 'text-left'
                        }`}
                      >
                        <div
                          className="text-[#00E5FF] text-3xl font-bold mb-2"
                          style={{ fontFamily: 'Orbitron, sans-serif' }}
                        >
                          {milestone.year}
                        </div>
                        <h3
                          className="text-xl font-bold text-white mb-3 leading-tight"
                          style={{ fontFamily: 'Rajdhani, sans-serif' }}
                        >
                          {milestone.title}
                        </h3>
                        <p
                          className="text-[#C0C7CF] text-sm leading-relaxed max-w-md"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          {milestone.description}
                        </p>
                      </div>
                    </div>

                    <div
                      className={
                        isLeft ? 'order-2 pl-12' : 'text-right pr-12 order-1'
                      }
                    >
                      <div
                        className={`inline-flex items-center justify-center w-16 h-16 bg-[#050505] border border-[#00E5FF]/40 ${
                          isLeft ? '' : 'ml-auto'
                        }`}
                      >
                        {/* Self‑closing <i> to avoid JSX parsing issues */}
                        <i
                          className={`${milestone.icon} text-2xl text-[#00E5FF]`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Desktop dot */}
                  <div className="hidden md:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                    <div className="w-5 h-5 bg-[#00E5FF] rounded-full border-4 border-[#111111] shadow-[0_0_15px_rgba(0,229,255,0.4)]"></div>
                  </div>

                  {/* Mobile layout */}
                  <div className="md:hidden flex gap-6 pl-14">
                    <div className="absolute left-6 top-2 -translate-x-1/2">
                      <div className="w-4 h-4 bg-[#00E5FF] rounded-full border-[3px] border-[#111111] shadow-[0_0_10px_rgba(0,229,255,0.4)]"></div>
                    </div>
                    <div>
                      <div
                        className="text-[#00E5FF] text-xl font-bold mb-1"
                        style={{ fontFamily: 'Orbitron, sans-serif' }}
                      >
                        {milestone.year}
                      </div>
                      <h3
                        className="text-lg font-bold text-white mb-2 leading-tight"
                        style={{ fontFamily: 'Rajdhani, sans-serif' }}
                      >
                        {milestone.title}
                      </h3>
                      <p
                        className="text-[#C0C7CF] text-sm leading-relaxed"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {milestone.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
